// Űrlapmezők a ds.css osztályaival.
//
// Minden mező valódi `input`/`textarea` + hozzá tartozó `label` — az átadási
// leírás szerint `div`-re kötött onClick nem elfogadható, mert a Tab átugorja.
// A címke `for` attribútuma köti a mezőhöz, így a képernyőolvasó is felolvassa.

import { t } from './i18n';
import { h } from './dom';

let nextId = 0;
function fieldId(prefix: string): string {
  nextId += 1;
  return `${prefix}-${nextId}`;
}

export type FieldControl<T extends HTMLElement = HTMLElement> = {
  element: HTMLElement;
  input: T;
  /// Hibaüzenet megjelenítése a mező alatt; `null` elrejti.
  setError: (message: string | null) => void;
  /// Az érték érvényes-e; hibás esetben megjeleníti az üzenetet és fókuszál.
  validate: () => boolean;
};

function wrap(options: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  control: HTMLElement;
  extra?: HTMLElement;
}): { element: HTMLElement; error: HTMLElement } {
  const error = h('span', { class: 'errtx', style: { display: 'none' } });
  return {
    error,
    element: h(
      'div',
      { class: 'field' },
      h(
        'label',
        { class: 'flbl', for: options.id },
        h('span', { text: options.label }),
        options.required ? h('span', { class: 'req', text: ' *' }) : null,
      ),
      options.control,
      options.extra ?? null,
      options.hint ? h('span', { class: 'hint', text: options.hint }) : null,
      error,
    ),
  };
}

export function textField(options: {
  label: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  value?: string;
  multiline?: boolean;
  maxLength?: number;
}): FieldControl<HTMLInputElement | HTMLTextAreaElement> {
  const id = fieldId('f');
  const maxLength = options.maxLength;

  const input = options.multiline
    ? (h('textarea', {
        class: 'input ta',
        id,
        rows: '4',
        placeholder: options.placeholder,
        maxlength: maxLength ? String(maxLength) : undefined,
      }) as HTMLTextAreaElement)
    : (h('input', {
        class: 'input',
        id,
        type: 'text',
        placeholder: options.placeholder,
        maxlength: maxLength ? String(maxLength) : undefined,
      }) as HTMLInputElement);

  if (options.value) input.value = options.value;

  const counter = maxLength
    ? h('span', { class: 'counter', text: `${input.value.length} / ${maxLength}` })
    : null;

  const { element, error } = wrap({
    id,
    label: options.label,
    required: options.required,
    hint: options.hint,
    control: input,
    extra: counter ? h('div', { class: 'row', style: { justifyContent: 'flex-end' } }, counter) : undefined,
  });

  const setError = (message: string | null) => {
    error.textContent = message ?? '';
    error.style.display = message ? '' : 'none';
    input.classList.toggle('err', message !== null);
  };

  input.addEventListener('input', () => {
    if (counter && maxLength) counter.textContent = `${input.value.length} / ${maxLength}`;
    if (input.value.trim().length > 0) setError(null);
  });

  return {
    element,
    input,
    setError,
    validate: () => {
      if (options.required && input.value.trim().length === 0) {
        setError(t('field.required'));
        input.focus();
        return false;
      }
      setError(null);
      return true;
    },
  };
}

export function selectField(options: {
  label: string;
  required?: boolean;
  hint?: string;
  choices: { value: string; label: string }[];
  value?: string;
}): FieldControl<HTMLSelectElement> {
  const id = fieldId('s');
  const input = h(
    'select',
    { class: 'input', id },
    ...options.choices.map((c) =>
      h('option', { value: c.value, text: c.label, selected: c.value === options.value ? 'selected' : undefined }),
    ),
  ) as HTMLSelectElement;

  const { element, error } = wrap({
    id,
    label: options.label,
    required: options.required,
    hint: options.hint,
    control: input,
  });

  const setError = (message: string | null) => {
    error.textContent = message ?? '';
    error.style.display = message ? '' : 'none';
  };

  return {
    element,
    input,
    setError,
    validate: () => {
      if (options.required && input.value.length === 0) {
        setError(t('field.pickValue'));
        input.focus();
        return false;
      }
      setError(null);
      return true;
    },
  };
}

export function dateTimeField(options: {
  label: string;
  required?: boolean;
  hint?: string;
  value?: Date;
  withTime?: boolean;
}): FieldControl<HTMLInputElement> {
  const id = fieldId('d');
  const input = h('input', {
    class: 'input',
    id,
    type: options.withTime === false ? 'date' : 'datetime-local',
  }) as HTMLInputElement;

  if (options.value) {
    // A datetime-local helyi idot var, ezert az eltolast le kell vonni.
    const local = new Date(options.value.getTime() - options.value.getTimezoneOffset() * 60000);
    input.value = local.toISOString().slice(0, options.withTime === false ? 10 : 16);
  }

  const { element, error } = wrap({
    id,
    label: options.label,
    required: options.required,
    hint: options.hint,
    control: input,
  });

  const setError = (message: string | null) => {
    error.textContent = message ?? '';
    error.style.display = message ? '' : 'none';
  };

  return {
    element,
    input,
    setError,
    validate: () => {
      if (options.required && input.value.length === 0) {
        setError(t('field.pickDate'));
        input.focus();
        return false;
      }
      setError(null);
      return true;
    },
  };
}

/// Számmező nap/óra/perc bontáshoz és dinamikus mezőkhöz.
export function numberField(options: {
  label: string;
  required?: boolean;
  min?: number;
  max?: number;
  width?: string;
  /// A tervek a számokat monospace-ben mutatják (gyári szám, garancia, nyomás):
  /// így a számjegyek egymás alá esnek, és a hosszuk összemérhető.
  mono?: boolean;
}): FieldControl<HTMLInputElement> {
  const id = fieldId('n');
  const input = h('input', {
    class: options.mono ? 'input mono' : 'input',
    id,
    type: 'number',
    min: options.min === undefined ? undefined : String(options.min),
    max: options.max === undefined ? undefined : String(options.max),
    style: options.width ? { width: options.width } : undefined,
  }) as HTMLInputElement;

  const { element, error } = wrap({
    id,
    label: options.label,
    required: options.required,
    control: input,
  });

  const setError = (message: string | null) => {
    error.textContent = message ?? '';
    error.style.display = message ? '' : 'none';
  };

  return {
    element,
    input,
    setError,
    validate: () => {
      if (options.required && input.value.trim().length === 0) {
        setError(t('field.required'));
        input.focus();
        return false;
      }
      setError(null);
      return true;
    },
  };
}

/// Több mező egyszerre — az első hibásnál megáll és oda fókuszál.
export function validateAll(fields: FieldControl<never>[] | FieldControl<HTMLElement>[]): boolean {
  for (const field of fields) {
    if (!field.validate()) return false;
  }
  return true;
}
